import type { intlFormatPropTypes } from "react-intl";

import { getCloudinaryTextData } from "../../../../../../../components/src/shared-components/inputs/data/cloudinary-data";

import {
  CLOUDINARY_CLOUD_NAME,
  CLOUDINARY_IMAGE_UPLOAD_PREFIX,
  CLOUDINARY_UPLOAD_PRESET,
  MENU_ALLOWED_FORMATS,
  MENU_MAX_FILE_SIZE,
} from "./MenuField.consts";
import type { CloudinaryGlobal, CloudinaryUploadInfo } from "./MenuField.types";

/**
 * Opens the Cloudinary upload widget (loaded globally in index.html) for a
 * single menu file. Resolves with the uploaded file's info, or `undefined`
 * if the host closes the widget without uploading.
 */
const openMenuUploadWidget = (intl: intlFormatPropTypes) =>
  new Promise<CloudinaryUploadInfo | undefined>((resolve, reject) => {
    const { cloudinary } = window as unknown as {
      cloudinary?: CloudinaryGlobal;
    };

    if (!cloudinary) {
      reject(new Error("Cloudinary widget library is not loaded."));
      return;
    }

    let uploaded: CloudinaryUploadInfo | undefined;
    let settled = false;

    const widget = cloudinary.createUploadWidget(
      {
        cloud_name: CLOUDINARY_CLOUD_NAME,
        upload_preset: CLOUDINARY_UPLOAD_PRESET,
        client_allowed_formats: MENU_ALLOWED_FORMATS,
        max_file_size: MENU_MAX_FILE_SIZE,
        multiple: false,
        max_files: 1,
        sources: ["local"],
        show_powered_by: false,
        theme: "minimal",
        language: "en",
        text: { en: getCloudinaryTextData(intl) },
      },
      (error, result) => {
        if (settled) return;

        if (error) {
          settled = true;
          widget.close();
          reject(error);
          return;
        }

        if (result.event === "success") {
          uploaded = result.info;
          return;
        }

        if (result.event === "close" || result.event === "abort") {
          settled = true;
          widget.close();
          resolve(uploaded);
        }
      },
    );

    widget.open();
  });

/**
 * A first-page image of a PDF menu stored on Cloudinary, which renders a PDF
 * page when the same asset is requested as `.jpg`. None for other URLs.
 */
const getMenuPdfPreviewUrl = (menuUrl: string) =>
  menuUrl.startsWith(CLOUDINARY_IMAGE_UPLOAD_PREFIX) && /\.pdf$/i.test(menuUrl)
    ? menuUrl.replace(/\.pdf$/i, ".jpg")
    : undefined;

export { getMenuPdfPreviewUrl, openMenuUploadWidget };
